import type { ShownPeriod } from "@stb/jurisdiction-au/income-tax"

import type { PayCopy, SourceKind } from "../../components/landing/pay-calculator"
import type { ModelCopy } from "../../components/landing/pay-calculator-model"
import { formatDateZh } from "./dates"

/**
 * Chinese text of the pay / HELP / Medicare calculator (T1320). Terms follow docs/design/landing/copy-zh.md.
 * Numbers are never written here: every amount, rate and date is passed in by the component from the engine.
 */

/** "fortnight" -> "两周" (used as "每两周"). */
const UNIT: Record<ShownPeriod, string> = { week: "周", fortnight: "两周", month: "月", year: "年" }
const aShown = (show: ShownPeriod) => `每${UNIT[show]}`

const SOURCE_LABEL: Record<SourceKind, string> = {
  super: "ATO，super guarantee（雇主 super 强制缴费率）",
  deduction: "ATO，工作相关开支标准扣除额（standard deduction）",
  resident: "ATO，澳大利亚居民税率",
  foreign: "ATO，外国居民税率",
  "working-holiday": "ATO，working holiday maker 税率",
  lito: "ATO，低收入税务抵免（LITO）",
  medicare: "ATO，低收入者 Medicare levy 减免",
  help: "ATO，学生和培训贷款最低还款收入及费率",
}

export const PAY_COPY_ZH: PayCopy = {
  emphasis: {
    pay: { title: "你的到手收入", label: "到手收入", short: "到手收入" },
    help: { title: "你的 HELP 还款", label: "HELP 还款", short: "HELP 还款" },
    medicare: { title: "你的 Medicare levy", label: "Medicare levy", short: "Medicare levy" },
  },
  periods: {
    year: "年",
    month: "月",
    fortnight: "两周",
    week: "周",
    day: "日",
    hour: "小时",
  },
  shown: { week: "周", fortnight: "两周", month: "月", year: "年" },
  per: (what, show) => `${aShown(show)}${what}`,
  aShown,
  sourceLabel: (kind) => SOURCE_LABEL[kind],
  checkedOn: formatDateZh,
  shortcuts: {
    button: "键盘快捷键",
    title: "键盘快捷键",
    actions: [
      "按一档调整金额",
      "按十档调整",
      "按十分之一档调整",
      "立即更新结果",
      "在发薪周期之间切换",
      "显示这些快捷键",
      "关闭此列表",
    ],
    changeBy: (amount) => `每次调整金额 ${amount}`,
    only: "仅在计算器获得焦点时有效。",
    hintAmount: "金额",
    hintPeriod: "周期",
    hintAll: "全部快捷键",
  },
  hintChangeBy: (amount) => `每次调整 ${amount}`,
  yourPay: "你的收入",
  payBeforeTax: "税前收入",
  yearlyGross: (amount) => `每年税前 ${amount}。`,
  paidPer: "发薪周期",
  quickSalaries: "常用年薪",
  presetChip: (k) => `A$${(k * 1000).toLocaleString("en-AU")}`,
  superLegend: "Super（雇主退休金）",
  superExcl: "不含",
  superIncl: "已含",
  incomeYear: "财年",
  yearOption: (fy) => {
    const start = Number(fy.slice(0, 4))
    return `${fy.replace("-", "–")} 财年（${start}年7月1日 – ${start + 1}年6月30日）`
  },
  hoursLabel: "每周工时",
  daysLabel: "每周工作天数",
  moreOptions: "更多选项",
  moreHint: {
    help: "税务居民身份、第二份工作",
    other: "HELP 债务、税务居民身份、第二份工作",
  },
  helpTitle: "我有 HELP（高等教育贷款计划）或其他学生贷款",
  helpHint: "加入强制还款",
  residencyLabel: "税务居民身份",
  residency: {
    resident: "澳大利亚税务居民",
    foreign: "外国居民",
    "working-holiday": "打工度假签证持有人（working holiday maker）",
  },
  otherLabel: "另一份工作的年收入",
  otherHint: "那份工作已使用免税额（tax-free threshold），所以这一份按你的最高税率计税。",
  medicareTitle: (rate) => `计入 ${rate} 的 Medicare levy`,
  medicareHint:
    "如果你获得 Medicare levy 豁免，例如医疗豁免，或无权享受 Medicare 福利，请取消勾选。全年为外国居民的人不缴此税。",
  deductionTitle: (max) => `计入 ${max} 标准扣除额（standard deduction，默认关闭）`,
  deductionHint: (fy) =>
    `ATO 会在你的 ${fy} 财年报税表中自动适用：仅限居民，针对已预扣税的工资、薪金及类似收入。你申报的工作相关开支会抵减它，且不适用于个体经营利润等经营收入。勾选即计入。`,
  riseQuestion: "加薪后会怎样？",
  riseValue: (amount) => `每年 +${amount}`,
  riseValueText: (amount) => `每年多 ${amount}`,
  riseNoText: "没有加薪",
  riseLine: (show) => [`${aShown(show)}到手 `, "，增加 ", ""],
  riseKeep: (cents, help) =>
    `每多赚 A$1，你保留 ${cents} 分；其余${help ? "用于缴税、Medicare levy 和 HELP 还款" : "用于缴税和 Medicare levy"}。`,
  riseEmpty: "拖动滑块，看看加薪后税后能多拿多少。",
  riseSaid: (takeHome, rise, show) => `${aShown(show)}到手 ${takeHome}，增加 +${rise}`,
  showPer: "显示周期",
  figureCaption: (label, fromJob, show) =>
    `${aShown(show)}${label}${fromJob ? "（仅本份工作）" : ""}`,
  rates: ["平均税率 ", " · 边际税率 ", ""],
  ratesEmpty: (dash) => `平均税率 ${dash} · 边际税率 ${dash}`,
  payBeforeTaxShort: "税前收入",
  superOnTop: "Super，雇主另付",
  youKeep: (share) => `你保留 ${share}`,
  splitRight: (pct, helpOn) =>
    `${pct} 用于缴税${helpOn ? "、Medicare levy 和 HELP 还款" : "和 Medicare levy"}`,
  whereGoes: (show) => `你的收入去向（${aShown(show)}）`,
  rowTax: "所得税（已扣抵免）",
  rowMedicare: "Medicare levy",
  rowHelp: "HELP 还款",
  rowHelpOff: "未计入",
  rowTakeHome: "到手收入",
  rowSuper: (rate) => `雇主 super（${rate}，另付）`,
  medicareSingle: (v) =>
    `单身人士：应税收入不超过 ${v.lower} 时不缴 Medicare levy；超出部分按 ${v.shade} 计，超过 ${v.upper} 后全额按 ${v.rate} 缴纳。`,
  medicareOff: "未计入 Medicare levy，按你的选择。请先确认你符合豁免条件。",
  medicareOffSource: "ATO，Medicare levy 豁免",
  deductionIncluded: (amount) =>
    `已计入 ${amount} 标准扣除额；雇主每期可能多预扣一点，差额在报税时退回。`,
  provisional: "暂定",
  provisionalFallback: "部分规则为暂定。",
  copyLink: "复制本结果的链接",
  localNote: "在你的浏览器中运行，不会向我们发送任何数据。",
  copied: "链接已复制",
  copyFailed: "复制失败，请改为从浏览器地址栏复制。",
  chartsLabel: "图表中的结果",
  bracket: {
    title: "你的税率档",
    marginal: "边际税率",
    plusLevy: (rate) => `，另加 ${rate} Medicare levy`,
    axis: "应税收入，单位：A$ 千元",
    invalid: "输入有效金额，查看你的税率档。",
    columns: ["档位（应税收入）", "税率", "你在此档缴的税"],
    tableCaption: (taxable, fy) =>
      `按档位计算的所得税${taxable === undefined ? "" : `（应税收入 ${taxable}）`}，${fy} 财年`,
  },
  cut: {
    title: (fy) => `你的 ${fy} 财年减税`,
    plain: "你的减税",
    line: (more, perShown, show) => `每年${more ? "多拿" : "少拿"} · ${aShown(show)} ${perShown}`,
    keyTax: "所得税和 Medicare levy",
    keySave: "节省",
    comes: (amount) => `这 ${amount} 的来源`,
    rateChanges: "税率调整",
    rateChange: (from, to) => `${from} 调整为 ${to}`,
    deduction: (max) => `新增 ${max} 标准扣除额`,
    showTable: "以表格显示",
    noPrevious: (fy) => `计算器没有 ${fy} 财年之前的数据可供比较。`,
    invalid: "输入有效金额，对比这两个财年。",
    columns: ["财年", "所得税", "Medicare levy", "到手收入"],
    tableCaption: (gross, previous, current) =>
      `${gross ?? "这份收入"}的税款，${previous} 财年与 ${current} 财年`,
  },
  disclaimer: {
    lead: "仅为估算，不构成税务建议。假设这是你唯一的收入（除非你填了另一份工作）、你单身且没有受抚养人，也没有其他扣除额或抵免。已含低收入税务抵免（LITO）和 Medicare levy 低收入减免。不含 Medicare levy surcharge（附加费）、SAPTO（老年人和领养老金者税务抵免）、salary sacrifice（薪资打包）和 fringe benefits（附加福利）。需要这些，或你有家庭，请使用 ",
    estimator: "ATO 个人所得税估算器（英文）",
    medicare: " ATO 的 Medicare levy 计算器涵盖家庭、SAPTO 和附加费：",
    medicareLink: "ATO Medicare levy 计算器（英文）",
  },
  how: {
    summary: "我们是怎么算的",
    sub: (fy) => `${fy} 财年，全年金额`,
    enter: "输入有效金额，查看计算过程。",
    payslip:
      "雇主每期按 ATO 税率表预扣税款，所以工资单上的数字可能相差几澳元。我们按全年算出税款再平摊；差额在你报税时结清。",
  },
  working: {
    pay: (gross, rate, employerSuper) =>
      `本份工作每年 ${gross}（不含 super）。雇主 super ${rate} = ${employerSuper}，另付。`,
    deduction: (notApplied, amount, taxable) =>
      `标准扣除额：${notApplied ? "未适用" : amount}，所以应税收入为 ${taxable}。`,
    tax: (v) => `所得税：${v.base} + ${v.rate} × (${v.taxable} − ${v.from}) = ${v.tax}。`,
    lito: (amount) => `低收入税务抵免（LITO）：${amount}。`,
    medicareNone: "Medicare levy：此居民身份不缴。",
    medicareOff: "Medicare levy：按你的选择未计入。",
    medicare: (rate, amount) => `Medicare levy：应税收入的 ${rate}，低收入者有减免 = ${amount}。`,
    help: (amount) => `学生贷款还款（按你的应税收入）：${amount}。`,
    takeHome: (v) => [
      `到手收入：${v.gross} − ${v.tax} + ${v.lito} − ${v.levy} − ${v.loan} = `,
      ` 每年。每个周期的金额为全年金额除以 52、26 或 12，并四舍五入到分。边际税率 ${v.marginal} 是你下一澳元要缴的税加 Medicare levy，不含学生贷款还款。`,
    ],
  },
  help: {
    title: "你的 HELP 还款档位",
    wholeIncome: "按你全部还款收入计算",
    overBand: "对超出该档起点的每一澳元",
    axis: "用作还款收入的应税收入，单位：A$ 千元",
    turnOn: "请勾选 HELP 选项，查看你的还款额。",
    combined: (taxable, repayment) =>
      `你的合并应税收入 ${taxable} 落在这一档。每年 ${repayment} 只是这份工作分摊的部分。`,
    under: (taxable) => `应税收入 ${taxable} 低于最低还款收入，所以无需还款。`,
    repay: (taxable, repayment) => `应税收入 ${taxable} 时，你每年还款 ${repayment}。`,
    invalid: "输入有效金额，查看你的还款档位。",
    tableCaption: (fy) => `${fy} 财年 HELP 还款档位`,
    columns: ["还款收入", "强制还款", "你所在的档位"],
    here: "你在这一档",
  },
  medicareFigure: {
    title: "你的 Medicare levy 与低收入减免",
    yearSingle: "每年，单身人士",
    axis: "应税收入，单位：A$ 千元",
    invalid: "输入有效金额，查看你的 Medicare levy。",
    noLevyShown:
      "对外国居民或打工度假签证持有人，本计算器不收 Medicare levy，所以上方曲线显示的是居民需缴的金额。",
    combined: (taxable) =>
      `你的合并应税收入是 ${taxable}。显示的 Medicare levy 只是这份工作分摊的部分。`,
    atOrUnder: (lower) => `你的应税收入不超过 ${lower}，无需缴 Medicare levy。`,
    reduction: (rate) => `你的应税收入处于减免区间，只需缴全额 ${rate} Medicare levy 的一部分。`,
    full: (upper, rate) => `你的应税收入超过 ${upper}，需缴全额 ${rate} Medicare levy。`,
    tableCaption: (fy) => `${fy} 财年按应税收入计算的 Medicare levy（单身人士）`,
    columns: ["应税收入", "Medicare levy"],
    upTo: (lower) => `不超过 ${lower}`,
    nil: "免缴",
    shade: (shade, lower) => `超出 ${lower} 的部分，每 A$1 缴 ${shade} 分`,
    over: (upper) => `超过 ${upper}`,
    fullRate: (rate) => `全部应税收入的 ${rate}`,
  },
  showTable: "以表格显示",
}

export const MODEL_COPY_ZH: ModelCopy = {
  outOfRange: "这个收入超出范围，请试试小一点的金额。",
  empty: "请输入税前收入，查看到手收入。",
  amount: "请输入 0 到 10,000,000 之间的金额，例如 85,000 或 32.50。",
  other: "另一份工作请输入 0 到 10,000,000 之间的金额，或保持为 0。",
  hours: "每周工时请填 1 到 100，例如 38。",
  days: "每周天数请填 1 到 7，例如 5。",
  answer: (v) =>
    `年薪 ${v.salary} 时，${v.fy} 财年每两周到手约 ${v.fortnight}，全年已扣所得税 ${v.tax} 和 Medicare levy（医疗保险税）${v.levy}。`,
  helpAnswer: (v) =>
    `年薪 ${v.salary} 并有 HELP（高等教育贷款计划）债务时，${v.fy} 财年强制还款约每年 ${v.annual}（每两周 ${v.fortnight}）。`,
  medicareAnswer: (v) =>
    `年薪 ${v.salary} 时，${v.fy} 财年 Medicare levy（医疗保险税）约每年 ${v.annual}（每两周 ${v.fortnight}），即 ${v.taxable} 应税收入的 ${v.rate}%。`,
  bracketNil: (t) => `应税收入 ${t} 时，每多赚一澳元不用缴所得税。`,
  bracketRate: (t, c) => `应税收入 ${t} 时，每多赚一澳元要缴 ${c} 分税。`,
  bracketTop: "这已是最高税率。",
  bracketNext: (v) => `${v.rate}% 的税率从 ${v.from} 起，还需再增加应税收入 ${v.toNext}。`,
  rangeOpen: (from) => `${from} 及以上`,
  rangeBetween: (from, to) => `${from} – ${to}`,
  ruleNil: "免缴",
  ruleWhole: (c) => `全部还款收入每 A$1 缴 ${c} 分`,
  ruleOver: (v) =>
    v.base
      ? `${v.base}，另加超出 ${v.from} 部分每 A$1 的 ${v.cents} 分`
      : `超出 ${v.from} 的部分，每 A$1 缴 ${v.cents} 分`,
}

/** What the ATO has not yet published: the zh text of each `PROVISIONAL_TEXT` entry (the engine decides which apply). */
export const PROVISIONAL_ZH: Record<
  | "medicare-levy-thresholds"
  | "foreign-resident-brackets"
  | "working-holiday-brackets"
  | "working-holiday-medicare-lito"
  | "standard-deduction",
  (v: { fy: string; prev: string; amount?: string }) => string
> = {
  "medicare-levy-thresholds": (v) =>
    `ATO 尚未公布 ${v.fy} 财年的 Medicare levy 门槛；暂定使用 ${v.prev} 财年的数值。`,
  "foreign-resident-brackets": (v) =>
    `ATO 尚未公布 ${v.fy} 财年的外国居民税率；暂定使用 ${v.prev} 财年的数值。`,
  "working-holiday-brackets": (v) =>
    `ATO 尚未公布 ${v.fy} 财年的 working holiday maker 税率；暂定使用 ${v.prev} 财年的数值。`,
  "working-holiday-medicare-lito": () =>
    "ATO 页面没有说明 working holiday maker 是否适用 Medicare levy 或低收入税务抵免（LITO）；我们暂定两者都不适用。",
  "standard-deduction": (v) =>
    `${v.fy} 财年的 ${v.amount ?? ""} 标准扣除额已宣布，但我们尚未对照法律条文核实（暂定）。`,
}

/** Text inside the (aria-hidden) charts. */
export const CHART_COPY_ZH = {
  you: (taxable: string) => `你 · ${taxable}`,
  noLevy: "免缴 Medicare levy",
}
